import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, } from 'react-router-dom';
import { SparklesIcon } from '@heroicons/react/24/outline';
import ResourcesPopup from './ResourcesPopup';
import { emitAITaskCompleted } from '../utils/aiEvents';
import { processingAIs } from '../utils/requestAIWithCache';
import { request } from '../utils/request';
import { updateGlobalResources, getGlobalResources, setCache } from '../utils/globalState';

interface ProcessingItem {
  key: string;
  res: any;
}

interface AIProcessingBubbleProps {
  className?: string;
}

const safeParseUrlFromKey = (key: string): { url?: string; code?: string } => {
  try {
    const obj = JSON.parse(key);
    return { url: String(obj.url || '').replace(/[`\s]/g, ''), code: String(obj.code || '') };
  } catch {
    const urlMatch = key.match(/"url"\s*:\s*["'`]\s*([^"'`]+)\s*["'`]/);
    const codeMatch = key.match(/"code"\s*:\s*["'`]\s*([^"'`]+)\s*["'`]/);
    return {
      url: urlMatch ? urlMatch[1].replace(/[`\s]/g, '') : undefined,
      code: codeMatch ? codeMatch[1] : undefined
    };
  }
};

const getStatusAndProgress = (res: any): { status: string; progress: number } => {
  const status = res?.result?.status ?? res?.status ?? 'processing';
  const progressRaw = res?.result?.progress ?? res?.progress ?? 0;
  const progress = typeof progressRaw === 'number' ? Math.max(0, Math.min(1, progressRaw)) : 0;
  return { status, progress };
};

const statusColor = (status: string) => {
  switch (status) {
    case 'completed':
      return 'bg-green-500';
    case 'failed':
      return 'bg-red-500';
    case 'pending':
      return 'bg-yellow-500';
    default:
      return 'bg-purple-500';
  }
};

const AIProcessingBubble: React.FC<AIProcessingBubbleProps> = ({ className }) => {

  const [aiRefreshTick, setAiRefreshTick] = useState(0);
  const location = useLocation();


  // 监控 processingAIs 数量（每秒更新一次）
  useEffect(() => {
    const timer = setInterval(() => {
      // 轮询处理中的 AI 任务：processingAIs 以 [dataStr, res] 成对存储
      if (processingAIs.length > 0) { 
        for (let i = 0; i < processingAIs.length; i++) {
          const res: any = processingAIs[i].res;
          const taskId = res?.result?.taskId ?? res?.taskId;

          if (taskId) {
            const idx = i; // 捕获当前对的起始索引
            request({
              url: `../back/giftmkr/ai/getResult?taskId=${taskId}`,
              method: 'GET'
            })
              .then((resp: any) => { 
                if (resp?.result?.status === 'completed') {
                  setCache(processingAIs[i].key, { timestamp: Date.now(), response: resp.result }); 
                  // 完成后从 processingAIs 中移除该条目（成对删除）
                  processingAIs.splice(idx, 1);
                  // 派发全局事件，通知其它组件触发相关逻辑（如 LayerRenderer 的 useEffect）
                  emitAITaskCompleted(resp?.result);
                  // 同步更新计数 
                  setAiRefreshTick((t) => t + 1);

                } else if (resp?.result?.status === 'failed') {
                  // 完成后从 processingAIs 中移除该条目（成对删除）
                  processingAIs.splice(idx, 1);
                  // 失败同样派发事件，让订阅方有机会重试或刷新
                  emitAITaskCompleted(resp?.result);
                  setAiRefreshTick((t) => t + 1);
                } else if (resp?.result?.status === 'processing') {
                  processingAIs[i].res = resp.result;
                  // 进度更新时也触发刷新
                  setAiRefreshTick((t) => t + 1);
                } else if (!resp?.result) {
                  if (Date.now() - processingAIs[i].timestamp > 1000 * 180) {
                    // 1分钟未更新，移除该条目
                    processingAIs.splice(idx, 1);
                    setAiRefreshTick((t) => t + 1);
                  }
                }
              })
              .catch((err: any) => {
                console.warn('Polling AI result failed:', err);
              });
          }
        }
      }
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const bubbles = useMemo(() => {
    return (processingAIs || [])
      .map((it) => {
        const { url, code } = safeParseUrlFromKey(String(it.key || ''));
        const { status, progress } = getStatusAndProgress(it.res);
        return { url, code, status, progress };
      })
      .filter((b) => !!b.url);
  }, [processingAIs, aiRefreshTick]);

  // Draggable floating card position and handlers
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ x: number; y: number }>({ x: 16, y: 16 });
  const [dragging, setDragging] = useState(false);
  const dragOffset = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [anchorPos, setAnchorPos] = useState<{ left: number; top: number }>(() => ({
    left: typeof window !== 'undefined' ? Math.round(window.innerWidth / 2) : 0,
    top: 30,
  }));

  useEffect(() => {
    // Initialize near top-right after first mount
    try {
      const initialX = Math.max(16, (typeof window !== 'undefined' ? window.innerWidth : 800) - 580);
      setPos({ x: initialX, y: 16 });
    } catch { }
  }, []);

  const clampPos = (nx: number, ny: number) => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 800;
    const h = typeof window !== 'undefined' ? window.innerHeight : 600;
    const cw = cardRef.current?.offsetWidth ?? 260;
    const ch = cardRef.current?.offsetHeight ?? 80;
    const x = Math.min(Math.max(0, nx), Math.max(0, w - cw));
    const y = Math.min(Math.max(0, ny), Math.max(0, h - ch));
    return { x, y };
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const nx = e.clientX - dragOffset.current.dx;
      const ny = e.clientY - dragOffset.current.dy;
      setPos(clampPos(nx, ny));
    };
    const onMouseUp = () => setDragging(false);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [dragging]);

  const startDragMouse = (e: React.MouseEvent) => {
    setDragging(true);
    dragOffset.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
  };

  const startDragTouch = (e: React.TouchEvent) => {
    const t = e.touches[0];
    if (!t) return;
    setDragging(true);
    dragOffset.current = { dx: t.clientX - pos.x, dy: t.clientY - pos.y };
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (!dragging) return;
    const t = e.touches[0];
    if (!t) return;
    const nx = t.clientX - dragOffset.current.dx;
    const ny = t.clientY - dragOffset.current.dy;
    setPos(clampPos(nx, ny));
  };

  // 点击卡片外的空白处时自动关闭 ResourcesPopup
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (!isResourcesOpen) return;
      const target = e.target as Node | null;
      const card = cardRef.current;
      if (card && target && !card.contains(target) && getGlobalResources().length > 0) {
        setIsResourcesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside, true);
    document.addEventListener('touchstart', handleClickOutside, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('touchstart', handleClickOutside, true);
    };
  }, [isResourcesOpen]);


  // 检查全局资源是否为空，如果为空则自动打开 ResourcesPopup
  useEffect(() => {
    const globalResources = getGlobalResources();
    if (globalResources.length === 0 && location.pathname.includes('/products')) {
      setIsResourcesOpen(true);
    }
  }, [location.pathname]);


  // 始终显示气泡卡片（即使没有处理中的任务）

  return (
    <>
      <div
        ref={cardRef}
        className={
          className ||
          "fixed inline-block w-fit z-[1000] rounded-xl border border-gray-200 shadow-lg bg-white/70 backdrop-blur-sm"
        }
        style={{ left: pos.x, top: pos.y }}
        onMouseDown={startDragMouse}
        onTouchStart={startDragTouch}
        onTouchMove={onTouchMove}
        onTouchEnd={() => setDragging(false)}
      >
        {/* Card content */}
        <div className="px-2 py-2 cursor-move select-none">

          {1 && (

            <div className="flex space-x-2 overflow-x-auto max-w-[70vw] p-1">
              {/* Open Resources Popup button */}
              <button
                ref={buttonRef}
                type="button"
                className="relative flex items-center justify-center w-10 h-10 rounded-full border border-purple-700 bg-purple-600 hover:bg-purple-700 shadow-md"
                aria-label="Open resources"
                title="Open resources"
                onClick={(e) => {
                  e.stopPropagation();
                  try {
                    const rect = buttonRef.current?.getBoundingClientRect();
                    if (rect) {
                      setAnchorPos({ left: Math.round(rect.left), top: Math.round(rect.bottom) });
                    }
                  } catch { }
                  // Toggle resources popup: if open, close; otherwise open
                  setIsResourcesOpen((prev) => !prev);
                }}
              >
                <SparklesIcon className={`h-5 w-5 text-white ${processingAIs.length > 0 ? 'animate-spin' : ''}`} />
                {getGlobalResources().length > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {getGlobalResources().length}
                  </span>
                )}
              </button>

              {bubbles.map((b, idx) => (
                <div
                  key={`${b.code || idx}-${idx}`}
                  className="relative w-10 h-10 rounded-full overflow-hidden border border-gray-200 shadow-md bg-white/80"
                >
                  {b.url ? (
                    <img src={b.url} alt={b.code || 'AI'} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100" />
                  )}
                  <span
                    className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full ${statusColor(
                      b.status
                    )} border border-white`}
                  ></span>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] leading-4 text-center">
                    {b.status === 'completed' ? '100%' : `${Math.round(b.progress * 100)}%`} {b.status}
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Resources Popup placed below the FolderIcon button */}
          <ResourcesPopup
            isOpen={isResourcesOpen}
            variant="anchored"
            anchorPosition={anchorPos}
            onChangeResource={(resources) => {
              // 更新全局资源状态
              updateGlobalResources(resources);
            }}
            onClose={() => setIsResourcesOpen(false)}
          />
          {isResourcesOpen && (
            // 背后阴影，提升前景弹窗的视觉层级
            <div
              className="fixed inset-0 bg-gray-900/40 backdrop-blur-[1px] z-40"
              onClick={() => setIsResourcesOpen(false)}
              aria-label="Close resources popup backdrop"
              role="button"
            />
          )}
        </div>
      </div>
    </>
  );
};

export default AIProcessingBubble;